/**
 * Initial Load Hooks
 * TanStack Query hooks for initial load status and management.
 * @packageDocumentation
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  resumeInitialLoad,
  cancelInitialLoad,
  triggerInitialLoad,
  fetchInitialLoadStatus,
  type TriggerInitialLoadRequest,
} from '@/api/initial-load';

// ============================================================================
// Query Keys
// ============================================================================

export const initialLoadKeys = {
  all: ['initial-load'] as const,
  status: (mappingIds?: number[]) => [...initialLoadKeys.all, 'status', mappingIds] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to fetch initial load status with conditional polling.
 * Polls every 5s when active jobs exist, stops when all idle.
 *
 * @param { number[] } mappingIds The ids of the mappings to track.
 * @returns { object } Returns TanStack Query result with status data, keyed by the mapping id as a string.
 */
export const useInitialLoadStatus = (mappingIds: number[]) => {
  const query = useQuery({
    enabled: mappingIds.length > 0,
    queryKey: initialLoadKeys.status(mappingIds),
    queryFn: () => fetchInitialLoadStatus(mappingIds),
    refetchInterval: (query) => {
      const data = query.state.data;

      if (!data) {
        return false;
      }

      // Poll when any mapping has an active job
      const hasActiveJobs = Object.values(data.statuses).some((s) => s.activeJobId !== null);

      return hasActiveJobs ? 5000 : false;
    },
  });

  return query;
};

/**
 * Hook to trigger an initial load.
 * Invalidates status query on success.
 *
 * @returns { object } Returns TanStack mutation for triggering loads.
 */
export const useTriggerInitialLoad = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: TriggerInitialLoadRequest) => triggerInitialLoad(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: initialLoadKeys.all });
    },
  });
};

/**
 * Hook to cancel an initial load.
 * Invalidates status query on success.
 *
 * @returns { object } Returns TanStack mutation for cancelling loads.
 */
export const useCancelInitialLoad = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => cancelInitialLoad(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: initialLoadKeys.all });
    },
  });
};

/**
 * Hook to resume a paused or failed initial load.
 * Invalidates status query on success.
 *
 * @returns { object } Returns TanStack mutation for resuming loads.
 */
export const useResumeInitialLoad = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobId: string) => resumeInitialLoad(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: initialLoadKeys.all });
    },
  });
};
